import React, { useEffect, useState, Suspense } from 'react'
import type { Table } from '@stratascratch/ui/components/DatasetTableTypes'
import { useAuth } from '@stratascratch/authentication'
import { CodeType } from '@stratascratch/code-editor'
import {
  executeCode,
  executeAlgorithmicCode,
  executeVisualCode,
} from '../lib/api'
import { ExecuteCodeParams } from './CodeEditorWidget/BaseCodeEditorWidget'
import { Test } from './CodeEditorWidget/TestTabs'

const BaseCodeEditorWidget = React.lazy(
  () => import('./CodeEditorWidget/BaseCodeEditorWidget'),
)

const ATTEMPT_STORAGE_KEY = 'stsc_b_exec'

interface Attempt {
  t: number // time
  a: number // attempt number
}

function getAttempt() {
  const value = localStorage.getItem(ATTEMPT_STORAGE_KEY)
  if (value) {
    return JSON.parse(value) as Attempt
  }
  return { t: 0, a: 0 }
}

export function saveAttempt(value: Attempt) {
  localStorage.setItem(ATTEMPT_STORAGE_KEY, JSON.stringify(value))
}

function checkAuthorization(): boolean {
  const attempt = getAttempt()

  if (!attempt || Date.now() - 24 * 3600 * 1000 > attempt.t) {
    saveAttempt({
      t: Date.now(),
      a: 0,
    })
    return checkAuthorization()
  }

  return attempt.a < 10
}

const codeTypeMapping = {
  pgsql: 'SQL',
  python: 'PYTHON',
  mssql: 'MSSQL',
  mysql: 'MYSQL',
  r: 'R',
} as const

interface Props {
  value: {
    questionClass: 'Coding Question' | 'Algorithm Question' | 'Visual Question'
    languageSnippets: {
      code: string
      language: keyof typeof codeTypeMapping
    }[]
    questionId: number
    questionUrl: string
    questionTables: Table[]
    checkSolution: boolean
    tests?: Test[]
  }
}

function CodeEditorWidget({ value }: Props) {
  const [isMounted, setIsMounted] = useState(false)
  const { isAuthenticated } = useAuth()
  const [isAuthorized, setIsAuthorized] = useState(false)
  const [selectedTest, setSelectedTest] = useState(value.tests?.[0])

  useEffect(() => {
    setIsAuthorized(checkAuthorization())
    setIsMounted(true)
  }, [value])

  const handleExecuteCode = async (params: ExecuteCodeParams) => {
    if (!isAuthenticated && !checkAuthorization()) {
      setIsAuthorized(false)
      return null
    }

    try {
      switch (value.questionClass) {
        case 'Algorithm Question':
          return executeAlgorithmicCode({
            ...params,
            test: selectedTest,
            code_type: 2,
          })

        case 'Visual Question':
          return executeVisualCode(params)

        case 'Coding Question':
        default:
          return executeCode(params)
      }
    } catch (err) {
      console.error(err)
      setIsAuthorized(false)
      return null
    } finally {
      if (!isAuthenticated) {
        const attempt = getAttempt()
        saveAttempt({ ...attempt, a: attempt.a + 1 })
      }
    }
  }

  if (!isMounted) return null

  const questionTables = value.questionTables?.map((t) => t.name)
  const codeSnippets = value.languageSnippets.map((snip) => ({
    code: snip.code || '',
    codeType:
      value.questionClass === 'Algorithm Question' ||
      value.questionClass === 'Visual Question'
        ? ('PYTHON' as CodeType)
        : (codeTypeMapping[
            snip.language.toLowerCase() as keyof typeof codeTypeMapping
          ] as CodeType),
  }))

  return (
    <Suspense fallback={<div>Loading editor...</div>}>
      <BaseCodeEditorWidget
        isAuthorized={isAuthenticated || isAuthorized}
        executeCode={handleExecuteCode}
        questionClass={value.questionClass}
        questionId={value.questionId}
        questionUrl={value.questionUrl}
        questionTables={
          value.questionClass === 'Coding Question' ? questionTables : undefined
        }
        checkSolution={value.checkSolution ?? true}
        codeSnippets={codeSnippets}
        algorithmTests={{
          tests: value.tests,
          selectedTest,
          setSelectedTest,
        }}
      />
    </Suspense>
  )
}

export default CodeEditorWidget
